import { useMemo, useState } from 'react';
import { DsButton, DsSegmented } from '@/modules/internal-v2/ds';
import { useGetAllUsers } from '@/api/user/user.hooks';
import { UserDetail } from '@/api/user/user.types';
import { ACCOUNT_TYPE, AccountType } from '@/constants/account-type';
import { ContainedPage } from '@/modules/internal-v2/shared/contained-page.component';
import { UserAccessEditor } from './components/user-access-editor.component';
import './users.css';

type Filter = AccountType | 'ALL';

const FILTER_LABELS: Record<Filter, string> = {
  [ACCOUNT_TYPE.INTERNAL]: 'Intern',
  [ACCOUNT_TYPE.EXTERNAL]: 'Extern',
  ALL: 'Alle',
};

const FILTER_ORDER: Filter[] = [
  ACCOUNT_TYPE.INTERNAL,
  ACCOUNT_TYPE.EXTERNAL,
  'ALL',
];

export function UsersPage() {
  const { data: users, isLoading, isError } = useGetAllUsers();
  const [selected, setSelected] = useState<UserDetail | null>(null);
  const [filter, setFilter] = useState<Filter>(ACCOUNT_TYPE.INTERNAL);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (users ?? [])
      .filter((user) => filter === 'ALL' || user.accountType === filter)
      .filter(
        (user) => term.length === 0 || user.email.toLowerCase().includes(term),
      );
  }, [users, filter, search]);

  const filterOptions = useMemo(() => {
    const all = users ?? [];
    const counts: Record<Filter, number> = {
      ALL: all.length,
      [ACCOUNT_TYPE.INTERNAL]: all.filter(
        (u) => u.accountType === ACCOUNT_TYPE.INTERNAL,
      ).length,
      [ACCOUNT_TYPE.EXTERNAL]: all.filter(
        (u) => u.accountType === ACCOUNT_TYPE.EXTERNAL,
      ).length,
    };
    return FILTER_ORDER.map((value) => ({
      value,
      label: FILTER_LABELS[value],
      count: counts[value],
    }));
  }, [users]);

  return (
    <ContainedPage
      toolbar={
        <div className="users-toolbar-content">
          <h1 className="users-title">Benutzer</h1>
          <DsSegmented
            className="users-filter-tabs"
            options={filterOptions}
            value={filter}
            onChange={setFilter}
            ariaLabel="Benutzer-Typ-Filter"
          />
          <input
            type="search"
            className="ds-input users-search"
            placeholder="E-Mail filtern…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="users-count">
            {users ? `${filtered.length} angezeigt` : ''}
          </span>
        </div>
      }
      contained
    >
      {isLoading && <div className="users-empty">Lädt…</div>}
        {isError && (
          <div className="users-empty">
            Benutzer konnten nicht geladen werden.
          </div>
        )}
        {!isLoading && !isError && filtered.length === 0 && (
          <div className="users-empty">
            {search
              ? 'Keine Benutzer mit dieser E-Mail.'
              : 'Keine Benutzer in dieser Auswahl.'}
          </div>
        )}
        {filtered.length > 0 && (
          <table className="ds-table users-table">
            <thead>
              <tr>
                <th>E-Mail</th>
                <th>Kontotyp</th>
                <th>Rollen</th>
                <th>Datensichtbarkeit</th>
                <th className="num">SAP-Code</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((user) => (
                <tr
                  key={user.id}
                  className="users-row"
                  onClick={() => setSelected(user)}
                >
                  <td className="users-email">{user.email}</td>
                  <td>
                    {user.accountType === ACCOUNT_TYPE.EXTERNAL ? (
                      <span className="ds-pill muted">Extern</span>
                    ) : user.isAdmin ? (
                      <span className="ds-pill pos">Administrator</span>
                    ) : (
                      <span className="ds-pill">Intern</span>
                    )}
                  </td>
                  <td>
                    <div className="users-role-chips">
                      {user.assignedRoles.length === 0 ? (
                        <span className="users-muted">—</span>
                      ) : (
                        user.assignedRoles.map((role) => (
                          <span key={role.id} className="users-role-chip">
                            {role.name}
                          </span>
                        ))
                      )}
                    </div>
                  </td>
                  <td>
                    {user.accountType === ACCOUNT_TYPE.EXTERNAL
                      ? '—'
                      : user.businessPartnerScope === 'ALL'
                        ? 'Alle Geschäftspartner'
                        : 'Eigene Geschäftspartner'}
                  </td>
                  <td className="num">{user.salesPersonCode ?? '—'}</td>
                  <td className="users-actions">
                    <DsButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(user);
                      }}
                    >
                      Zugriff verwalten
                    </DsButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

      {selected && (
        <UserAccessEditor user={selected} onClose={() => setSelected(null)} />
      )}
    </ContainedPage>
  );
}
